import { createCar, stepPhysics, type Car } from "./physics/car";
import { createInput, type InputState } from "./input/input";
import { createReplayRecorder, type ReplayRecorder } from "./input/replay";
import {
  initWebGL,
  renderScene,
  runPostProcess,
  ensurePostProcess,
  setCameraMode,
  getCameraMode,
  setActiveRenderer,
  type Renderer,
  type CameraMode,
} from "./render/renderer";
import { createGPTrack, createMonzaTrack, createSpaTrack, type Track } from "./track/track";
import { createMinimap, renderMinimap, type Minimap } from "./hud/minimap";
import {
  createParticleSystem,
  emitSmoke,
  updateParticles,
  renderParticles,
  type ParticleSystem,
} from "./render/particles";

// --- Lap timing ---

interface LapState {
  // Index of the nearest centerline point last frame
  lastNearestIndex: number;
  // Current lap start time (seconds)
  lapStartTime: number;
  // Current lap time (seconds)
  currentLapTime: number;
  // Best lap time (seconds), 0 = no lap completed
  bestLapTime: number;
  // Last completed lap time
  lastLapTime: number;
  // Lap count
  lapCount: number;
  // Has the car moved far enough from start to allow crossing detection
  armed: boolean;
}

// --- Game state ---

interface Game {
  car: Car;
  track: Track;
  input: InputState;
  replay: ReplayRecorder;
  renderer: Renderer;
  minimap: Minimap;
  particles: ParticleSystem;
  lap: LapState;
  lastTime: number;
  accumulator: number;
  running: boolean;
}

const PHYSICS_DT = 1 / 120; // Fixed 120Hz physics step

// --- Track list ---

const tracks = [
  { name: "Grand Prix", create: createGPTrack },
  { name: "Monza", create: createMonzaTrack },
  { name: "Spa", create: createSpaTrack },
];
let currentTrackIndex = 0;

function switchTrack(game: Game, canvas: HTMLCanvasElement): void {
  currentTrackIndex = (currentTrackIndex + 1) % tracks.length;
  const trackDef = tracks[currentTrackIndex];
  const newTrack = trackDef.create();

  game.track = newTrack;
  game.renderer = initWebGL(canvas, newTrack);
  setActiveRenderer(game.renderer);
  game.minimap = createMinimap(newTrack);
  game.particles = createParticleSystem(game.renderer.gl);

  // Reset car to new track start
  game.car.x = newTrack.startX;
  game.car.y = newTrack.startY;
  game.car.angle = newTrack.startAngle;
  game.car.vx = 0;
  game.car.vy = 0;
  game.car.angularVel = 0;

  // Reset lap state
  const now = performance.now() / 1000;
  game.lap.lastNearestIndex = newTrack.startFinishIndex;
  game.lap.lapStartTime = now;
  game.lap.currentLapTime = 0;
  game.lap.bestLapTime = 0;
  game.lap.lastLapTime = 0;
  game.lap.lapCount = 0;
  game.lap.armed = false;

  // Update track name HUD
  const trackNameEl = document.getElementById("track-name");
  if (trackNameEl) {
    trackNameEl.textContent = trackDef.name;
  }
}

function init(): Game {
  const canvas = document.getElementById("game") as HTMLCanvasElement;
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  window.addEventListener("resize", () => {
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
  });

  const track = createGPTrack();
  const renderer = initWebGL(canvas, track);
  const car = createCar(track.startX, track.startY, track.startAngle);
  const input = createInput();
  const replay = createReplayRecorder();
  const minimap = createMinimap(track);
  const particles = createParticleSystem(renderer.gl);

  const lap: LapState = {
    lastNearestIndex: track.startFinishIndex,
    lapStartTime: performance.now() / 1000,
    currentLapTime: 0,
    bestLapTime: 0,
    lastLapTime: 0,
    lapCount: 0,
    armed: false,
  };

  return {
    car,
    track,
    input,
    replay,
    renderer,
    minimap,
    particles,
    lap,
    lastTime: performance.now() / 1000,
    accumulator: 0,
    running: true,
  };
}

function findNearestCenterlineIndex(car: Car, track: Track): number {
  const cl = track.centerline;
  let bestDist = Infinity;
  let bestIndex = 0;

  for (let i = 0; i < cl.length; i++) {
    const dx = car.x - cl[i].x;
    const dy = car.y - cl[i].y;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = i;
    }
  }

  return bestIndex;
}

function updateLapTiming(game: Game, now: number): void {
  const { car, track, lap } = game;
  const cl = track.centerline;
  const sfIdx = track.startFinishIndex;
  const nearestIdx = findNearestCenterlineIndex(car, track);

  lap.currentLapTime = now - lap.lapStartTime;

  // Arm the crossing detector once the car is far enough from the start/finish
  // (prevents immediate crossing on spawn)
  const distFromSF = Math.min(
    Math.abs(nearestIdx - sfIdx),
    cl.length - Math.abs(nearestIdx - sfIdx)
  );

  if (!lap.armed && distFromSF > cl.length * 0.1) {
    lap.armed = true;
  }

  // Detect crossing: car moves from just before start/finish to just after
  if (lap.armed) {
    const prev = lap.lastNearestIndex;
    // Check if we crossed the start/finish line (wrapping around the centerline)
    const crossedForward =
      prev < sfIdx && nearestIdx >= sfIdx && nearestIdx - prev < cl.length / 2;
    const crossedWrap =
      prev > sfIdx + cl.length * 0.8 && nearestIdx < sfIdx + cl.length * 0.2 &&
      nearestIdx < prev;

    if (crossedForward || crossedWrap) {
      const lapTime = lap.currentLapTime;
      if (lapTime > 5) {
        // Ignore very short "laps" (glitches)
        lap.lastLapTime = lapTime;
        lap.lapCount++;
        if (lap.bestLapTime === 0 || lapTime < lap.bestLapTime) {
          lap.bestLapTime = lapTime;
        }
        lap.lapStartTime = now;
        lap.currentLapTime = 0;
        lap.armed = false;
      }
    }
  }

  lap.lastNearestIndex = nearestIdx;
}

function formatLapTime(seconds: number): string {
  if (seconds <= 0) return "--:--.---";
  const mins = Math.floor(seconds / 60);
  const secs = seconds - mins * 60;
  return `${mins}:${secs.toFixed(3).padStart(6, "0")}`;
}

function update(game: Game): void {
  const now = performance.now() / 1000;
  let dt = now - game.lastTime;
  game.lastTime = now;

  // Clamp dt to prevent spiral of death
  if (dt > 0.1) dt = 0.1;

  game.accumulator += dt;

  // Record input if recording
  if (game.replay.recording) {
    game.replay.frames.push({
      t: now,
      throttle: game.input.throttle,
      brake: game.input.brake,
      steer: game.input.steer,
    });
  }

  // Fixed-timestep physics
  while (game.accumulator >= PHYSICS_DT) {
    stepPhysics(game.car, game.input, game.track, PHYSICS_DT);
    game.accumulator -= PHYSICS_DT;
  }

  // Lap timing
  updateLapTiming(game, now);

  // Interpolation alpha for rendering
  const alpha = game.accumulator / PHYSICS_DT;

  // Render (pass gyro roll for camera tilt on mobile)
  const { view, projection } = renderScene(game.renderer, game.car, game.track, alpha, game.input.gyroRoll, game.input.steer);

  // Particles: emit smoke, update, render (still inside pp.sceneFbo)
  emitSmoke(game.particles, game.car, game.input, dt);
  updateParticles(game.particles, dt);
  renderParticles(game.particles, view, projection);

  // Post-processing: bloom + motion blur → composite to canvas. Skipped when
  // adaptive quality has turned it off (slow device); in that case the scene
  // was already rendered straight to the canvas.
  if (game.renderer.postProcessEnabled) {
    const speedMs = Math.sqrt(game.car.vx ** 2 + game.car.vy ** 2);
    const speedFrac = Math.min(speedMs / 83.3, 1); // 83.3 m/s ≈ 300 km/h
    runPostProcess(game.renderer, speedFrac);
  }

  // Minimap
  renderMinimap(game.minimap, game.car);

  // HUD
  const speedKmh = Math.sqrt(game.car.vx ** 2 + game.car.vy ** 2) * 3.6;
  document.getElementById("speed")!.textContent = `${Math.round(speedKmh)} km/h`;

  // Lap HUD
  const lapEl = document.getElementById("lap");
  if (lapEl) {
    lapEl.textContent =
      `Lap ${game.lap.lapCount + 1}  ${formatLapTime(game.lap.currentLapTime)}`;
  }
  const bestEl = document.getElementById("best");
  if (bestEl) {
    bestEl.textContent = game.lap.bestLapTime > 0
      ? `Best: ${formatLapTime(game.lap.bestLapTime)}`
      : "";
  }
  const lastEl = document.getElementById("last");
  if (lastEl) {
    lastEl.textContent = game.lap.lastLapTime > 0
      ? `Last: ${formatLapTime(game.lap.lastLapTime)}`
      : "";
  }

  // Camera mode HUD
  const camEl = document.getElementById("cam-mode");
  if (camEl) {
    camEl.textContent = CAMERA_MODE_LABELS[getCameraMode(game.renderer)];
  }
}

// --- FPS tracking ---
const frameTimes: number[] = [];
let lastFpsUpdate = 0;
const fpsEl = document.getElementById("fps");

// --- Adaptive-quality governor ---
// Watches a rolling window of frame deltas and sheds post-processing when the
// device can't sustain a reasonable frame rate. Disable/enable thresholds are
// split (hysteresis) so the toggle doesn't flap. At a vsync-capped 60fps the
// mean delta (~16.7ms) sits in the keep-PP-on zone, so capable hardware always
// keeps bloom; only sustained slowness (the throttled software-GL probe, weak
// integrated GPUs) drops it.
const GOV_WINDOW = 20;
const GOV_MIN_SAMPLES = 5;
const GOV_DISABLE_MS = 24; // sustained mean above this (~<42fps) → PP off
const GOV_ENABLE_MS = 19; // sustained mean below this (~>52fps) → PP back on
const govDeltas: number[] = [];

function updateAdaptiveQuality(game: Game, delta: number): void {
  const r = game.renderer;
  if (!r.allowPostProcess) return; // PP gated off up front — nothing to govern
  govDeltas.push(delta);
  if (govDeltas.length > GOV_WINDOW) govDeltas.shift();
  if (govDeltas.length < GOV_MIN_SAMPLES) return;
  const mean = govDeltas.reduce((s, d) => s + d, 0) / govDeltas.length;
  if (r.postProcessEnabled && mean > GOV_DISABLE_MS) {
    r.postProcessEnabled = false;
  } else if (!r.postProcessEnabled && mean < GOV_ENABLE_MS) {
    r.postProcessEnabled = true;
  }
}

let govLastNow = 0;

function gameLoop(game: Game, now: number = performance.now()): void {
  if (!game.running) return;

  // Adaptive quality: feed this frame's delta to the governor.
  if (govLastNow > 0) updateAdaptiveQuality(game, now - govLastNow);
  govLastNow = now;

  // Track frame times for FPS
  frameTimes.push(now);
  // Keep last 60 frames
  if (frameTimes.length > 60) frameTimes.shift();

  // Update FPS display every 250ms
  if (now - lastFpsUpdate > 250 && frameTimes.length > 1) {
    const elapsed = frameTimes[frameTimes.length - 1] - frameTimes[0];
    const fps = ((frameTimes.length - 1) / elapsed) * 1000;
    const frameTime = elapsed / (frameTimes.length - 1);
    if (fpsEl) {
      fpsEl.textContent = `${Math.round(fps)} fps\n${frameTime.toFixed(1)} ms`;
      fpsEl.style.whiteSpace = "pre";
    }
    lastFpsUpdate = now;
  }

  update(game);

  // Build post-processing AFTER the first frame has painted, so its shader
  // compiles + FBO allocations don't delay first paint (FCP/LCP/Speed Index).
  // setTimeout(0) yields to the browser to paint frame 1 before we block on it.
  // Skipped entirely on software renderers (allowPostProcess=false) — PP is
  // never built there.
  if (!ppInitScheduled && game.renderer.allowPostProcess) {
    ppInitScheduled = true;
    setTimeout(() => ensurePostProcess(game.renderer), 0);
  }

  requestAnimationFrame((t) => gameLoop(game, t));
}

let ppInitScheduled = false;

// --- Start ---

const game = init();
setActiveRenderer(game.renderer);

// Toggle camera mode with C key
const CAMERA_MODES: CameraMode[] = ["cockpit", "chase", "chase-north"];
const CAMERA_MODE_LABELS: Record<CameraMode, string> = {
  cockpit: "Cockpit",
  chase: "Chase",
  "chase-north": "Chase (North)",
};
window.addEventListener("keydown", (e) => {
  if (e.key === "c" || e.key === "C") {
    const current = getCameraMode(game.renderer);
    const idx = CAMERA_MODES.indexOf(current);
    const next = CAMERA_MODES[(idx + 1) % CAMERA_MODES.length];
    setCameraMode(game.renderer, next);
  }
});

// Toggle recording with R key
window.addEventListener("keydown", (e) => {
  if (e.key === "r" || e.key === "R") {
    game.replay.recording = !game.replay.recording;
    const rec = document.getElementById("rec")!;
    rec.style.display = game.replay.recording ? "block" : "none";
    if (!game.replay.recording && game.replay.frames.length > 0) {
      console.log(
        `Recording stopped. ${game.replay.frames.length} frames captured.`
      );
      console.log("Export with: copy(game.exportReplay())");
    } else {
      game.replay.frames = [];
      console.log("Recording started.");
    }
  }
});

// Switch track with T key
window.addEventListener("keydown", (e) => {
  if (e.key === "t" || e.key === "T") {
    const canvas = document.getElementById("game") as HTMLCanvasElement;
    switchTrack(game, canvas);
    console.log(`Switched to: ${tracks[currentTrackIndex].name}`);
  }
});

// Expose for console access, screenshot tooling, and E2E tests
(window as unknown as Record<string, unknown>).game = {
  perfStats: () => {
    if (frameTimes.length < 2) return { fps: 0, frameTimeMs: 0, frames: 0 };
    const elapsed = frameTimes[frameTimes.length - 1] - frameTimes[0];
    const fps = ((frameTimes.length - 1) / elapsed) * 1000;
    return { fps: Math.round(fps), frameTimeMs: elapsed / (frameTimes.length - 1), frames: frameTimes.length };
  },
  exportReplay: () => JSON.stringify(game.replay.frames),
  // Manual override of adaptive quality — for the perf harness and console use.
  setPostProcess: (on: boolean) => { game.renderer.postProcessEnabled = on; },
  isPostProcessEnabled: () => game.renderer.postProcessEnabled,
  // Capability gate: false on software renderers (the web-health SwiftShader
  // probe, GPU-less machines), where PP is never built.
  isPostProcessAllowed: () => game.renderer.allowPostProcess,
  car: game.car,
  lap: game.lap,
  input: game.input,
  track: game.track,
  // Render a full still frame to the canvas. renderScene draws into the
  // post-process scene FBO when PP is on, so we must run the composite pass too
  // — otherwise the canvas keeps showing whatever the live loop last composited
  // (stale, and identical across calls). Used by the screenshot/visual tooling.
  render: () => {
    renderScene(game.renderer, game.car, game.track, 0, 0, 0);
    if (game.renderer.postProcessEnabled) runPostProcess(game.renderer, 0);
  },
  stop: () => { game.running = false; },
  start: () => { game.running = true; gameLoop(game); },
  resetCar: () => {
    game.car.x = game.track.startX;
    game.car.y = game.track.startY;
    game.car.angle = game.track.startAngle;
    game.car.vx = 0;
    game.car.vy = 0;
    game.car.angularVel = 0;
    game.lap.lastNearestIndex = game.track.startFinishIndex;
    game.lap.lapStartTime = 0;
    game.lap.currentLapTime = 0;
    game.lap.bestLapTime = 0;
    game.lap.lastLapTime = 0;
    game.lap.lapCount = 0;
    game.lap.armed = false;
  },
  simulate: (steps: number, throttle: number, brake: number, steer: number) => {
    const inp = { throttle, brake, steer, gyroRoll: 0, gyroActive: false };
    for (let i = 0; i < steps; i++) {
      stepPhysics(game.car, inp, game.track, PHYSICS_DT);
    }
    return {
      x: game.car.x, y: game.car.y, angle: game.car.angle,
      vx: game.car.vx, vy: game.car.vy,
      speed: Math.sqrt(game.car.vx ** 2 + game.car.vy ** 2),
    };
  },
  simulateWithTiming: (steps: number, throttle: number, brake: number, steer: number) => {
    const inp = { throttle, brake, steer, gyroRoll: 0, gyroActive: false };
    let simTime = game.lap.lapStartTime + game.lap.currentLapTime;
    for (let i = 0; i < steps; i++) {
      stepPhysics(game.car, inp, game.track, PHYSICS_DT);
      simTime += PHYSICS_DT;
      updateLapTiming(game, simTime);
    }
    return {
      x: game.car.x, y: game.car.y, angle: game.car.angle,
      vx: game.car.vx, vy: game.car.vy,
      speed: Math.sqrt(game.car.vx ** 2 + game.car.vy ** 2),
      lap: {
        lapCount: game.lap.lapCount,
        bestLapTime: game.lap.bestLapTime,
        lastLapTime: game.lap.lastLapTime,
        currentLapTime: game.lap.currentLapTime,
        armed: game.lap.armed,
      },
    };
  },
};

gameLoop(game);
